import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { useSpaceGroupResource } from "@circle-react/contexts/useSpaceGroupResource";
import { useRemoveMemberFromSpaceGroup } from "@circle-react/hooks/useRemoveMemberFromSpaceGroup";
import { Modal } from "@circleco/compass/components/Modal";
import { Typography } from "@circleco/compass/components/Typography";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import type { RemoveSpaceGroupMemberModalProps } from "./types";

export const RemoveSpaceGroupMemberModalComponent = ({
  spaceGroupId,
  memberId,
  queryKey,
}: RemoveSpaceGroupMemberModalProps) => {
  const modal = useModal();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refetch } = useSpaceGroupResource(spaceGroupId);

  const { mutate: removeMember, isPending: isRemoving } =
    useRemoveMemberFromSpaceGroup({
      onError: (error: { message: string }) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        if (queryKey) {
          void queryClient.invalidateQueries({ queryKey });
        }
        void queryClient.invalidateQueries({ queryKey: ["community-members"] });
        void refetch?.();
        toast.success(t("space_groups.form.edit.members.members_removed"));
        void modal.hide();
      },
    });

  const handleConfirmRemove = () => {
    removeMember({ spaceGroupId, memberId });
  };

  const removeLabel = isRemoving
    ? t("space_groups.form.edit.members.removing")
    : t("space_groups.form.edit.members.remove_member");

  return (
    <Modal.Root
      open={modal.visible}
      onOpenChange={open => {
        if (!open && !isRemoving) void modal.hide();
      }}
      size="md"
    >
      <Modal.Content>
        <Modal.Header
          title={t("space_groups.form.edit.members.remove_from_space_group")}
          showCloseButton
        />
        <Modal.Body>
          <Typography variant="body-md" className="whitespace-pre-line">
            {t(
              "space_groups.form.edit.members.remove_from_space_group_description",
            )}
          </Typography>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
          }}
          primaryAction={{
            label: removeLabel,
            variant: "destructive",
            onClick: handleConfirmRemove,
            loading: isRemoving,
            disabled: isRemoving,
            shouldCloseModal: false,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
